import React, { useEffect, useMemo, useState } from 'react';
import { MutationStatus } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { Progress, Typography } from 'web-library';
import { useTranslation } from 'react-i18next/';
import RouteKey from '../../../routes/key';
import { CreateCandidateFormLocationState } from '../logic/useCreateCandidateForm';
import { getCreateCandidateFormData } from '../logic/useMyInfo';
import { parseLocationSearch } from '../../../utils/StringUtils';
import { CUSTOMIZED_UNAUTHORIZED_STATUS_CODE } from '../../../api/config/misc';
import { usePostMyInfoToken } from '../../../api-refactored/user/usePostMyInfoToken';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';

const { auth: authKey, profile } = RouteKey.child;
const { login, createAccount, createCandidate: createCandidateAuth } = RouteKey.auth.child;
const { createCandidate, candidateDetail, requestForAmendment } = RouteKey.profile.child;

const UNKNOWN_ERROR_MSG = 'profile->>MyInfoPage.An unknown error has occured';

interface MyInfoCallbackUrlParams {
  code?: string | null;
  state?: string | null;
  errorDescription?: string | null;
  alternateErrorDescription?: string | null;
}

export default function MyInfoPage() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const {
    code: urlParamCode,
    state: urlParamState,
    errorDescription: urlParamErrorDescription,
    // chrome on macbook return in a different parameter
    'error-description': alternativeUrlParamErrorDescription,
  } = parseLocationSearch(location.search);

  const myInfoCallbackUrlParams = useMemo<MyInfoCallbackUrlParams>(
    () => ({
      code: urlParamCode,
      state: urlParamState,
      errorDescription: urlParamErrorDescription,
      alternateErrorDescription: alternativeUrlParamErrorDescription,
    }),
    [urlParamCode, urlParamState, urlParamErrorDescription, alternativeUrlParamErrorDescription]
  );

  const iExamMasterCodeQuery = useGetIExamMasterCode();
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [fieldsNullButMandatory, setFieldsNullButMandatory] = useState<string[]>([]);
  const [isUnknownError, setIsUnknownError] = useState<boolean>(false);

  const { mutate: retrieveMyInfoMutate, status: retrieveMyInfoStatus } = usePostMyInfoToken({
    onSuccess: (data) => {
      if (iExamMasterCodeQuery.isSuccess) {
        const {
          createCandidateFormData,
          fieldsWithError,
          fieldsMandatoryButNull,
        } = getCreateCandidateFormData(data, iExamMasterCodeQuery.data);

        const createCandidateLocationState: CreateCandidateFormLocationState = {
          formData: createCandidateFormData,
          credentialType: 'SINGPASS',
          hasMyInfoProfile: true,
        };

        setErrorFields(fieldsWithError);
        setFieldsNullButMandatory(fieldsMandatoryButNull);

        const dataAfterRedirectFromProfile = sessionStorage.getItem(
          'urlToBeRedirectedAfterLoggedIn'
        ) as string;
        const dataObjectAfterRedirectFromProfile = JSON.parse(dataAfterRedirectFromProfile);

        const dataToBePassedAfterRedirectBackToProfile = {
          ...createCandidateLocationState,
          redirectFromMyInfo: true,
        };
        if (
          dataObjectAfterRedirectFromProfile?.from?.redirectFromProfile &&
          dataObjectAfterRedirectFromProfile?.from?.isClickRequestForAmendmentButton
        ) {
          history.push(
            `/${profile}/${candidateDetail.route}/${requestForAmendment.route}`,
            dataToBePassedAfterRedirectBackToProfile
          );
        } else if (
          dataObjectAfterRedirectFromProfile?.from?.redirectFromProfile &&
          !dataObjectAfterRedirectFromProfile?.from?.isClickRequestForAmendmentButton
        ) {
          history.push(
            `/${profile}/${candidateDetail.route}`,
            dataToBePassedAfterRedirectBackToProfile
          );
        } else if (fieldsWithError.length === 0 && fieldsMandatoryButNull.length === 0) {
          history.push(
            `/${authKey}/${createAccount}/${createCandidateAuth}`,
            createCandidateLocationState
          );
        }
      }
    },
    onError: (err) => {
      const unknownErrorHandling = () => setIsUnknownError(true);
      switch (err.response?.status) {
        case CUSTOMIZED_UNAUTHORIZED_STATUS_CODE: {
          let message = t(UNKNOWN_ERROR_MSG);
          if (err.response?.data?.code === '1008') {
            message = err.response?.data?.message!;
          }
          history.push(`/${authKey}/${login}`, { message });
          break;
        }
        case 404: {
          if (err.response?.data?.code === '4003') {
            history.push(`/${profile}/${createCandidate.route}`, {
              hasMyInfoProfile: false,
              credentialType: 'SINGPASS',
            });
          }
          break;
        }
        case 417:
          if (err.response?.data?.code === '1201' && err.response?.data?.data!.url !== undefined) {
            window.location.href = err.response?.data?.data.url as string;
          } else {
            unknownErrorHandling();
          }
          break;
        default:
          unknownErrorHandling();
          break;
      }
    },
  });

  useEffect(() => {
    if (iExamMasterCodeQuery.isSuccess) {
      if (myInfoCallbackUrlParams.state && myInfoCallbackUrlParams.code) {
        retrieveMyInfoMutate({
          code: myInfoCallbackUrlParams.code,
          state: myInfoCallbackUrlParams.state,
        });
      } else {
        let message = myInfoCallbackUrlParams.errorDescription;
        if (!message) {
          message = myInfoCallbackUrlParams.alternateErrorDescription;
        }
        history.push(`/${authKey}/${login}`, {
          message: message ?? t(UNKNOWN_ERROR_MSG),
        });
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [iExamMasterCodeQuery.isSuccess]);

  return (
    <div>
      {(iExamMasterCodeQuery.isLoading ||
        retrieveMyInfoStatus === ('loading' as MutationStatus)) && <Progress color="secondary" />}

      {iExamMasterCodeQuery.error && <Typography>Error fetching Master Code</Typography>}

      {!!myInfoCallbackUrlParams.errorDescription && (
        <Typography>{myInfoCallbackUrlParams.errorDescription}</Typography>
      )}
      {retrieveMyInfoStatus === ('error' as MutationStatus) && (
        <Typography>{t('profile->>MyInfoPage.Error retrieving MyInfo data.')}</Typography>
      )}

      {errorFields.length > 0 && fieldsNullButMandatory.length === 0 && (
        <>
          <Typography>
            {t(
              'profile->>MyInfoPage.Could not find corresponding MyInfo data from Master Code: {{errorList}}',
              {
                errorList: JSON.stringify(errorFields),
              }
            )}
          </Typography>
          <Typography>{t('profile->>MyInfoPage.Please contact SEAB.')}</Typography>
        </>
      )}

      {fieldsNullButMandatory.length > 0 && (
        <Typography>
          {t(
            'profile->>MyInfoPage.{{fieldsNullButMandatory}} info is/are not available. Please update your MyInfo details before trying again.',
            {
              fieldsNullButMandatory: JSON.stringify(fieldsNullButMandatory),
            }
          )}
        </Typography>
      )}

      {isUnknownError && <Typography>{t(UNKNOWN_ERROR_MSG)}</Typography>}
    </div>
  );
}
